/**
 * Portal Page
 * 
 * Dashboard gallery view showing available dashboards.
 * Rendered inside AppLayout which provides the Header and optional sidebar.
 * 
 * Both admin and viewer users see dashboards grouped by folder in collapsible sections.
 * Admin view: Dashboards with click-to-open-in-Looker (external link indicator).
 * Viewer view: Dashboards with click-to-embed navigation (no external link indicator).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Alert,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { dashboardApi, embedApi, type Dashboard, type DashboardFolder } from '../services/api';
import { DashboardGrid } from '../components/DashboardGrid';
import { useAuth } from '../hooks/useAuth';
import { useSudo } from '../hooks/useSudo';

/**
 * Collapsible folder section for both admin and viewer
 */
function FolderSection({
  folder,
  onSelectDashboard,
  defaultExpanded = true,
  externalLink = false,
}: {
  folder: DashboardFolder;
  onSelectDashboard: (dashboard: Dashboard) => void;
  defaultExpanded?: boolean;
  externalLink?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          mb: expanded ? 2 : 0,
          py: 1,
          px: 1.5,
          borderRadius: 2,
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.15s',
        }}
      >
        <FolderOpenIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          {folder.name}
        </Typography>
        <Chip
          label={`${folder.dashboards.length} dashboard${folder.dashboards.length !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
          sx={{ height: 24, fontSize: '0.75rem' }}
        />
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <DashboardGrid
          dashboards={folder.dashboards}
          isLoading={false}
          onSelect={onSelectDashboard}
          externalLink={externalLink}
        />
      </Collapse>
    </Box>
  );
}

export function PortalPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { sudoUser, isSudoMode } = useSudo();

  // Both viewers and admins use folder-grouped structure
  // In sudo mode, fetch the target viewer's dashboards
  const {
    data: folders = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboards', sudoUser?.id ?? (isAdmin ? 'admin' : 'viewer')],
    queryFn: () => dashboardApi.getDashboardsByFolder(sudoUser?.id),
  });

  // Admin needs Looker host for direct links
  const { data: lookerHost } = useQuery({
    queryKey: ['lookerHost'],
    queryFn: embedApi.getLookerHost,
    enabled: isAdmin,
    staleTime: 1000 * 60 * 10,
  });

  // Viewer: navigate to embedded viewer
  const handleViewerSelect = (dashboard: Dashboard) => {
    navigate(`/viewer/${dashboard.id}?title=${encodeURIComponent(dashboard.title)}`);
  };

  // Admin: open dashboard directly in Looker in a new tab
  const handleAdminSelect = (dashboard: Dashboard) => {
    if (lookerHost) {
      window.open(`https://${lookerHost}/dashboards/${dashboard.id}`, '_blank');
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box component="main" sx={{ flex: 1, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Dashboards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSudoMode
                ? `Viewing as ${sudoUser?.firstName} ${sudoUser?.lastName} — Click to preview embedded dashboard`
                : isAdmin
                  ? 'All dashboards across user-assigned folders. Click to open in Looker.'
                  : 'Select a dashboard to view analytics'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Failed to load dashboards. Please try again.
            </Alert>
          )}

          {isLoading ? (
            <DashboardGrid dashboards={[]} isLoading onSelect={() => {}} />
          ) : folders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, maxWidth: 500, mx: 'auto' }}>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1.5 }}>
                No Folders Configured
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSudoMode
                  ? 'This viewer has no dashboard folders assigned. Assign folder IDs in the Users management page.'
                  : isAdmin
                    ? 'No dashboard folders have been assigned to any users yet. Assign folder IDs to users in the Users management page.'
                    : 'No dashboards are available for your account. Contact your administrator for access.'}
              </Typography>
            </Box>
          ) : (
            folders.map((folder) => (
              <FolderSection
                key={folder.id}
                folder={folder}
                onSelectDashboard={isAdmin && !isSudoMode ? handleAdminSelect : handleViewerSelect}
                defaultExpanded={!isAdmin || isSudoMode || folders.length <= 3}
                externalLink={isAdmin && !isSudoMode}
              />
            ))
          )}
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          &copy; 2026 Looker Embed Portal. Secure dashboard embedding with SSO.
        </Typography>
      </Box>
    </Box>
  );
}

export default PortalPage;
